'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * DNAHelix — Rotierende DNA-Doppelhelix
 *
 * Technik: Canvas 2D, 30fps, Sinuswellen-Simulation mit Depth-Effect
 * CPU-Impact: Low (~50 Punkte, einfache Math.sin-Berechnungen)
 *
 * Verwendung:
 *   <DNAHelixBackground>
 *     <div className="relative z-10">Content</div>
 *   </DNAHelixBackground>
 */
function DNAHelixBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  color1,
  color2,
  connectorColor,
  helixCount = 3,
  variant = 'auto',
  ...props
}) {
  const isDark = useBackgroundTheme(variant);
  const resolvedColor1 = color1 ?? (isDark ? '#06b6d4' : '#0284c7');
  const resolvedColor2 = color2 ?? (isDark ? '#ec4899' : '#db2777');
  const resolvedConnector = connectorColor ?? (isDark ? '#8b5cf6' : '#7c3aed');
  const bgClass = isDark ? 'bg-slate-950' : 'bg-white';
  const canvasRef = React.useRef(null);
  const animRef = React.useRef(null);
  const lastFrameRef = React.useRef(0);
  const timeRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const FPS = 30;
  const INTERVAL = 1000 / FPS;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w;
    canvas.height = h;

    const observer = new IntersectionObserver(
      ([entry]) => { pausedRef.current = !entry.isIntersecting; },
      { threshold: 0.01 },
    );
    observer.observe(canvas);

    const ro = new ResizeObserver(() => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
    });
    ro.observe(canvas);

    const hexToRgb = (hex) => {
      const h = hex.replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };
    const [r1, g1, b1] = hexToRgb(resolvedColor1);
    const [r2, g2, b2] = hexToRgb(resolvedColor2);
    const [rc, gc, bc] = hexToRgb(resolvedConnector);

    const STEPS = 50;           // Punkte pro Helix
    const AMPLITUDE = 0.12;     // Amplitude als Anteil der Breite

    const draw = (timestamp) => {
      animRef.current = requestAnimationFrame(draw);
      if (pausedRef.current) return;
      if (timestamp - lastFrameRef.current < INTERVAL) return;
      lastFrameRef.current = timestamp;

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      timeRef.current += 0.018 * speed;
      const t = timeRef.current;

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = isDark ? 'rgb(2,6,23)' : 'rgb(255,255,255)';
      ctx.fillRect(0, 0, w, h);

      // Draw multiple helices spread across the canvas
      const helixSpacing = w / (helixCount + 1);

      for (let hi = 0; hi < helixCount; hi++) {
        const cx = helixSpacing * (hi + 1);
        const amplitude = w * AMPLITUDE;
        const phaseOffset = (hi / helixCount) * Math.PI * 2;

        // Collect strand points
        const strand1 = [];
        const strand2 = [];

        for (let i = 0; i <= STEPS; i++) {
          const progress = i / STEPS;
          const y = progress * h;
          const phase = progress * Math.PI * 4 - t * 2 + phaseOffset;

          const x1 = cx + Math.sin(phase) * amplitude;
          const x2 = cx + Math.sin(phase + Math.PI) * amplitude;
          // depth: 0 = back, 1 = front
          const depth1 = (Math.sin(phase) + 1) / 2;
          const depth2 = (Math.sin(phase + Math.PI) + 1) / 2;

          strand1.push({ x: x1, y, depth: depth1 });
          strand2.push({ x: x2, y, depth: depth2 });
        }

        // Draw connectors (rungs) — only when strands are close
        for (let i = 0; i <= STEPS; i += 3) {
          const s1 = strand1[i];
          const s2 = strand2[i];
          const avgDepth = (s1.depth + s2.depth) / 2;
          const alpha = 0.15 + avgDepth * 0.4;

          // Connector thickness varies with depth
          const lineW = 0.5 + avgDepth * 1.5;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${rc},${gc},${bc},${alpha * opacity})`;
          ctx.lineWidth = lineW;
          ctx.moveTo(s1.x, s1.y);
          ctx.lineTo(s2.x, s2.y);
          ctx.stroke();
        }

        // Draw strand 1 as continuous path with varying opacity/width
        for (let i = 0; i < strand1.length - 1; i++) {
          const s = strand1[i];
          const sn = strand1[i + 1];
          const alpha = 0.2 + s.depth * 0.7;
          const lineW = 0.8 + s.depth * 2.5;

          ctx.beginPath();
          ctx.strokeStyle = `rgba(${r1},${g1},${b1},${alpha * opacity})`;
          ctx.lineWidth = lineW;
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(sn.x, sn.y);
          ctx.stroke();

          // Glow on near points
          if (s.depth > 0.8) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${r1},${g1},${b1},${0.1 * opacity})`;
            ctx.lineWidth = lineW * 6;
            ctx.moveTo(s.x, s.y);
            ctx.lineTo(sn.x, sn.y);
            ctx.stroke();
          }
        }

        // Draw strand 2
        for (let i = 0; i < strand2.length - 1; i++) {
          const s = strand2[i];
          const sn = strand2[i + 1];
          const alpha = 0.2 + s.depth * 0.7;
          const lineW = 0.8 + s.depth * 2.5;

          ctx.beginPath();
          ctx.strokeStyle = `rgba(${r2},${g2},${b2},${alpha * opacity})`;
          ctx.lineWidth = lineW;
          ctx.moveTo(s.x, s.y);
          ctx.lineTo(sn.x, sn.y);
          ctx.stroke();

          if (s.depth > 0.8) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${r2},${g2},${b2},${0.1 * opacity})`;
            ctx.lineWidth = lineW * 6;
            ctx.moveTo(s.x, s.y);
            ctx.lineTo(sn.x, sn.y);
            ctx.stroke();
          }
        }

        // Draw nodes at connector points
        for (let i = 0; i <= STEPS; i += 3) {
          const s1 = strand1[i];
          const s2 = strand2[i];

          [s1, s2].forEach((s, idx) => {
            const r = idx === 0 ? r1 : r2;
            const g = idx === 0 ? g1 : g2;
            const b = idx === 0 ? b1 : b2;
            const nodeR = 1 + s.depth * 3;
            const alpha = 0.3 + s.depth * 0.65;

            ctx.beginPath();
            ctx.arc(s.x, s.y, nodeR, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${r},${g},${b},${alpha * opacity})`;
            ctx.fill();
          });
        }
      }
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      observer.disconnect();
      ro.disconnect();
    };
  }, [speed, resolvedColor1, resolvedColor2, resolvedConnector, helixCount, opacity, isDark]);

  return (
    <div
      data-slot="dna-helix-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      {...props}
    >
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { DNAHelixBackground };
